import dayjs from "dayjs";
import { AVAILABILITY_TEXT } from "../constants/availability.constants";
import { SUPPLY_TYPE_PRE_ORDER } from "../types/variant.types";

export interface ShippingDateVariant {
    supplyType?: string;
    preOrderShippingDate?: string;
    shippingDate?: string;
    pricing?: { supplyType?: string; shippingDate?: string };
}

export class DateUtils {
    static isPreOrderVariant(
        variant: ShippingDateVariant | null | undefined
    ): boolean {
        const supplyType =
            variant?.supplyType ?? variant?.pricing?.supplyType ?? "";
        return supplyType.toLowerCase() === SUPPLY_TYPE_PRE_ORDER;
    }

    static isPreOrderLeadTimeText(value: string): boolean {
        const trimmed = value.trim();
        if (/^\d{4}-\d{2}-\d{2}(?:T|$)/.test(trimmed) && dayjs(trimmed).isValid()) return false;
        if (/week|month|day|eta/i.test(trimmed)) {
            return true;
        }
        if (/^\d+\s*-\s*\d+/.test(trimmed)) {
            return true;
        }
        return /[a-zA-Z]/.test(trimmed) && !dayjs(trimmed).isValid();
    }

    static formatPreOrderShippingDate(
        preOrderShippingDate: string | null | undefined,
        supplierDeliveryTerm?: string | null
    ): string {
        const text = preOrderShippingDate?.trim();
        const fallback = supplierDeliveryTerm?.trim();

        if (text && DateUtils.isPreOrderLeadTimeText(text)) {
            return text;
        }

        if (fallback) {
            return fallback;
        }

        if (text && dayjs(text).isValid()) {
            return DateUtils.formatShippingDate(text);
        }

        return text || AVAILABILITY_TEXT.EMPTY_DATE;
    }

    static getVariantShippingDateDisplay(
        variant: ShippingDateVariant | null | undefined,
        supplierDeliveryTerm?: string | null
    ): string {
        if (!variant) {
            return AVAILABILITY_TEXT.EMPTY_DATE;
        }

        if (DateUtils.isPreOrderVariant(variant)) {
            return DateUtils.formatPreOrderShippingDate(
                variant.preOrderShippingDate,
                supplierDeliveryTerm
            );
        }

        return DateUtils.formatShippingDate(
            variant.shippingDate ?? variant.pricing?.shippingDate
        );
    }

    static formatShippingDate(dateString: string | null | undefined): string {
        if (!dateString) return AVAILABILITY_TEXT.EMPTY_DATE;
        const today = dayjs().startOf("day");
        const targetDate = dayjs(dateString).startOf("day");
        const diffInDays = targetDate.diff(today, "day");
        if (diffInDays < 7) return AVAILABILITY_TEXT.ONE_WEEK;

        const weeks = Math.ceil(diffInDays / 7);

        if (weeks === 1) return AVAILABILITY_TEXT.ONE_WEEK;
        if (weeks <= 2) return AVAILABILITY_TEXT.TWO_WEEKS;
        if (weeks <= 3) return AVAILABILITY_TEXT.THREE_WEEKS;
        if (weeks <= 4) return AVAILABILITY_TEXT.FOUR_WEEKS;

        return AVAILABILITY_TEXT.ONE_MONTH;
    }

    static formatToLocalDate(dateString: string | null | undefined): string {
        if (!dateString) {
            return "N/A";
        }

        return dayjs(dateString).format("DD/MM/YYYY");
    }

    static formatToReadable(dateString: string | null | undefined): string {
        if (!dateString) {
            return "N/A";
        }

        return dayjs(dateString).format("MMMM D, YYYY");
    }

    static formatCustom(
        dateString: string | null | undefined,
        format: string
    ): string {
        if (!dateString) {
            return "N/A";
        }

        return dayjs(dateString).format(format);
    }

    static isValid(dateString: string | null | undefined): boolean {
        if (!dateString) {
            return false;
        }

        return dayjs(dateString).isValid();
    }

    static getDaysUntil(dateString: string | null | undefined): number {
        if (!dateString) {
            return 0;
        }

        const today = dayjs();
        const targetDate = dayjs(dateString);
        return targetDate.diff(today, "day");
    }
}
